/**
 * Helpers for the PCM_F32LE payload type (docs/PROTOCOL.md §3.1) and for making
 * decoder output fit the AudioContext.
 */

/** Split interleaved f32 samples into one Float32Array per channel. */
export function deinterleave(interleaved: Float32Array, channels: number): Float32Array[] {
  if (channels <= 0) return [];
  const frames = Math.floor(interleaved.length / channels);
  const planes: Float32Array[] = [];
  for (let c = 0; c < channels; c++) planes.push(new Float32Array(frames));

  for (let i = 0; i < frames; i++) {
    const base = i * channels;
    for (let c = 0; c < channels; c++) {
      // Non-null assertions would hide a real bug here; the bounds are computed
      // from `frames` so the reads are in range by construction.
      const plane = planes[c] as Float32Array;
      plane[i] = interleaved[base + c] as number;
    }
  }
  return planes;
}

/**
 * Decode a PCM_F32LE payload into planar channels.
 *
 * The payload is a view into the WebSocket buffer whose byte offset is 16 (the
 * header), so it is 4-byte aligned in practice — but a relay that repacked the
 * frame could break that, and `new Float32Array(buffer, offset)` throws on an
 * unaligned offset. Copy in that case rather than losing the frame.
 */
export function pcmFrameToPlanar(payload: Uint8Array, sampleCount: number): Float32Array[] {
  if (sampleCount <= 0) return [];
  const bytesPerChannel = sampleCount * Float32Array.BYTES_PER_ELEMENT;
  const channels = Math.floor(payload.byteLength / bytesPerChannel);
  if (channels < 1) return [];

  const total = channels * sampleCount;
  const aligned =
    payload.byteOffset % Float32Array.BYTES_PER_ELEMENT === 0
      ? new Float32Array(payload.buffer, payload.byteOffset, total)
      : new Float32Array(payload.slice(0, total * Float32Array.BYTES_PER_ELEMENT).buffer);

  return deinterleave(aligned, channels);
}

/** Peak absolute sample across all channels, for meters when no header peak exists. */
export function planarPeak(planes: readonly Float32Array[]): number {
  let peak = 0;
  for (const plane of planes) {
    for (let i = 0; i < plane.length; i++) {
      const value = Math.abs(plane[i] as number);
      if (value > peak) peak = value;
    }
  }
  return peak;
}

/**
 * Continuous linear resampler.
 *
 * Needed only when the browser refuses to give us a 48 kHz AudioContext — some
 * Bluetooth and USB devices pin the hardware rate. It keeps the last input sample
 * and the fractional read position across calls, so resampling frame by frame does
 * not click at every boundary the way a stateless resampler would.
 */
export class LinearResampler {
  private position = 0;
  /** Last sample of the block just consumed, per channel. */
  private tail: Float32Array;
  /** Last sample of the block *before* that — the `index === -1` neighbour. */
  private tailBefore: Float32Array;

  constructor(
    readonly channels: number,
    private ratio: number,
  ) {
    this.tail = new Float32Array(channels);
    this.tailBefore = new Float32Array(channels);
  }

  setRatio(ratio: number): void {
    if (ratio > 0 && Number.isFinite(ratio)) this.ratio = ratio;
  }

  reset(): void {
    this.position = 0;
    this.tail.fill(0);
    this.tailBefore.fill(0);
  }

  /** `ratio` is outputRate / inputRate. Consumes the whole input block. */
  process(input: readonly Float32Array[]): Float32Array[] {
    const inFrames = input[0]?.length ?? 0;
    if (inFrames === 0) return input.map(() => new Float32Array(0));

    this.rememberTail(input, inFrames);
    if (Math.abs(this.ratio - 1) < 1e-9) return input.slice();

    const step = 1 / this.ratio;
    // Only emit outputs whose interpolation window is inside this block; whatever
    // is left over is carried in `position` and picked up on the next call.
    const outFrames = Math.max(0, Math.floor((inFrames - 1 - this.position) / step) + 1);
    const out: Float32Array[] = [];
    for (let c = 0; c < this.channels; c++) out.push(new Float32Array(outFrames));

    for (let c = 0; c < this.channels; c++) {
      const plane = input[c] ?? input[0];
      const target = out[c];
      if (!plane || !target) continue;
      const previous = this.tailBefore[c] ?? 0;
      for (let i = 0; i < outFrames; i++) {
        const pos = this.position + i * step;
        const index = Math.floor(pos);
        const frac = pos - index;
        const a = index < 0 ? previous : (plane[index] ?? 0);
        const b = plane[index + 1] ?? plane[inFrames - 1] ?? 0;
        target[i] = a + (b - a) * frac;
      }
    }

    this.position = this.position + outFrames * step - inFrames;
    return out;
  }

  private rememberTail(input: readonly Float32Array[], inFrames: number): void {
    this.tailBefore.set(this.tail);
    for (let c = 0; c < this.channels; c++) {
      const plane = input[c] ?? input[0];
      this.tail[c] = plane ? (plane[inFrames - 1] ?? 0) : 0;
    }
  }
}
